import { Component, inject, HostListener, OnInit } from '@angular/core';
import { ActivatedRoute, Router, RouterOutlet } from '@angular/router';
import { MatIconRegistry } from '@angular/material/icon';

import { TranslateService } from '@ngx-translate/core';
import { ModuleMessageService } from './core/module-message.service';
import { I18nService } from './core/i18n.service';
import { ModuleStateService } from './module-state.service';
import { Subscription } from 'rxjs';
import { LoaderComponent } from "./shared/loader/loader.component";
import { AuthenticationService } from './core/authentication/authentication.service';
import { ApiService } from './core/api/api.service';

import { environment } from '../../../commondata/environments/environment';

import { CredentialsRequestMessage, GetInitialStateMessage } from '../../../commondata/messages/messages-dto';

import * as itIT from '../translations/it-IT.json';
import * as enGB from '../translations/en-GB.json';
import { LoaderService } from './shared/loader/loader.service';
import { isObjectLike } from 'lodash';

@Component({
    selector: 'app-root',
    imports: [RouterOutlet, LoaderComponent],
    templateUrl: './app.component.html',
    styleUrl: './app.component.scss'
})
export class AppComponent implements OnInit {
  title = 'pdf';
  paramsSubscription: Subscription | undefined;
  @HostListener('window:message', ['$event'])
  onMessage(event: any) {
    this.moduleMessageService.handleMessage(event);
  }
  private moduleMessageService = inject(ModuleMessageService);
  private i18nService = inject(I18nService);
  private translateService = inject(TranslateService);
  private matIconReg = inject(MatIconRegistry);
  private authenticationService = inject(AuthenticationService);
  private router = inject(Router);
  private loaderService = inject(LoaderService);

  constructor(
    private route: ActivatedRoute,
    private moduleStateService: ModuleStateService,
    private apiService: ApiService
  ) { }

  ngOnInit(): void {
    // Setup translations
    const translations: any = { 'it-IT': itIT, 'en-GB': enGB };
    this.i18nService.init(
      environment.defaultLanguage,
      environment.supportedLanguages,
      translations
    );
    environment.supportedLanguages.forEach((element) => {
      this.translateService.getTranslation(element);
    });

    // Set default icon stile
    this.matIconReg.setDefaultFontSetClass('material-symbols-rounded');

    this.loaderService.startLoading('generic', 'PLEASE_WAIT');

    this.initModule();

    // Initialize Matomo tracker GDPR-compliant
    // Verifichiamo di essere nel browser (e non in Server Side Rendering SSR)
    if (typeof window !== 'undefined') {
      const _paq = ((window as any)._paq = (window as any)._paq || []);
      // Applichiamo i comandi nativi sfruttando il motore asincrono di Matomo
      // _paq.push(['anonymizeIp']);
      _paq.push(['enableHeartBeatTimer', 15]);
      // _paq.push(['discardHashInUrl', true]);
      _paq.push(['disableCookies']);
      _paq.push(['disableBrowserFeatureDetection']);
      _paq.push(['disableCampaignParameters']);
      _paq.push(['setExcludedQueryParams', [
        "account", "accountnum", "address", "address1", "address2", "address3",
        "addressline1", "addressline2", "adres", "adresse", "adresse1", "adresse2",
        "adresse3", "adresse_email", "adresseemail", "adressepostale", "age", "alter",
        "auth", "authpw", "bic", "billingaddress", "billingaddress1", "billingaddress2",
        "calle", "cardnumber", "carte", "cartebancaire", "carteidentite", "cb", "cc",
        "ccc", "cccsc", "cccvc", "cccvv", "ccexpiry", "ccexpmonth", "ccexpyear", "ccname",
        "ccnumber", "cctype", "cell", "cellphone", "city", "civilite", "civilité", "cle",
        "clientid", "clientsecret", "clé", "codepostal", "company", "consumerkey",
        "consumersecret", "contrasenya", "contraseña", "courriel", "cp", "creditcard",
        "creditcardnumber", "cvc", "cvv", "datedenaissance", "dateexpiration", "datenaissance",
        "dateofbirth", "debitcard", "departement", "dirección", "dob", "domain",
        "département", "ebost", "email", "emailaddress", "emailadresse", "entreprise",
        "epos", "epost", "eposta", "exp", "expiration", "familyname", "firma", "firstname",
        "formlogin", "fullname", "gender", "genre", "geschlecht", "gst", "gstnumber",
        "handynummer", "hasło", "heslo", "iban", "ibanaccountnum", "ibanaccountnumber",
        "id", "identifiant", "identifier", "identitenationale", "indirizzo", "kartakredytowa",
        "kennwort", "keyconsumerkey", "keyconsumersecret", "konto", "kontonr", "kontonummer",
        "kredietkaart", "kreditkarte", "kreditkort", "lastname", "login", "mail", "mdp",
        "mobiili", "mobile", "mobilne", "mot_de_passe", "motdepasse", "nachname", "name",
        "nationalite", "nickname", "nom", "nomcomplet", "nomdefamille", "nomfamille",
        "nss", "numero_fiscal", "numerocarte", "numerocarteidentite", "numerocompte",
        "numerodecarte", "numerofiscal", "numeroidentite", "numeromobile", "numeropasseport",
        "numerosecuritesociale", "numerotelephone", "numerotva", "numfiscal", "numsecu",
        "numtva", "osoite", "parole", "pass", "passeport", "passord", "password",
        "passwort", "pasword", "paswort", "paword", "pays", "phone", "pin", "plz",
        "portable", "postalcode", "postcode", "postleitzahl", "prenom", "privatekey",
        "prénom", "publickey", "pw", "pwd", "pword", "pwrd", "questionsecrete", "region",
        "reponsesecrete", "rib", "rue", "secret", "secretclé", "secretq", "secretquestion",
        "securitesociale", "sexe", "shippingaddress", "shippingaddress1", "shippingaddress2",
        "signature", "siren", "siret", "socialsec", "socialsecuritynumber", "societe",
        "socsec", "sokak", "ssn", "steuernummer", "strasse", "street", "surname", "swift",
        "tax", "taxnumber", "tel", "telefon", "telefonnr", "telefonnummer", "telefono",
        "telephone", "titre", "token", "token_auth", "tokenauth", "tva", "téléphone",
        "ulica", "user", "username", "utilisateur", "vat", "vatnumber", "via", "ville",
        "voie", "vorname", "wachtwoord", "wagwoord", "webhooksecret", "website", "zip",
        "zipcode"
      ]]);
      // 2. FORZIAMO il tracciamento della pagina corrente e abilitiamo l'ascolto dei click sui link
      _paq.push(['trackPageView']);
      _paq.push(['enableLinkTracking']);
    }

  }

  private initModule() {
    // Viene usata la skip perche' il primo subscribe viene lanciato al caricamento del componente con valori vuoti
    // this.route.queryParamMap.pipe(skip(1)).subscribe(
    if (this.paramsSubscription) {
      this.paramsSubscription.unsubscribe();
    }
    this.paramsSubscription = this.route.queryParams.subscribe((params) => {

      const guid = params['guid'] ?? '';
      if (guid) {
        this.moduleStateService.guid = guid;
        this.moduleStateService.docId = params['id'] ?? '';
        const isLocked = (params['isLocked'] || ' ') === 'true';
        const openInView = (params['openInView'] || '') === 'true';
        const googleId = params['googleId'] || '';
        this.moduleStateService.googleId = googleId;
        let landingUrl = 'pdf';
        // Segna apertura documento
        this.apiService.addCustomDocStat(this.moduleStateService.docId, 'STAT_VIEWSHARED', this.authenticationService.getUserEmail()).subscribe(() => {
          console.log('Doc open');
        });
        // Inizializza messaggi con la shell
        if (openInView) {
          landingUrl = 'view-pdf';
        }
        this.moduleMessageService.initialize(this.moduleStateService.guid, landingUrl, isLocked);

        const noUser = params['noUser'] === 'true';
        if (noUser) {
          const url = 'view-pdf';
          if (this.authenticationService.isAuthenticated()) {
            this.authenticationService.logout();
          }
          this.router.navigate([url], { replaceUrl: true });
        } else {
          // Recupera le credenziali dalla shell
          const msg = new CredentialsRequestMessage(
            this.moduleStateService.guid,
            {}
          );
          (window as any).top.postMessage(msg, '*');
          this.moduleMessageService.sendMessage(new GetInitialStateMessage(this.moduleStateService.guid));
        }
      }
    });
  }
}
